/**
 * **Graph-Explorer** — der Wissensgraph zum Anfassen, für Plattform-Administratoren.
 *
 * Metadatengetrieben: die Typenliste, die Felder im Seitenpanel und die Kanten beim Ausklappen
 * kommen aus der tri-model-Registry über `/administration/graph/*`. Nichts davon ist hier
 * aufgezählt — eine Liste im Frontend wäre eine zweite Wahrheit über das Modell, und beim Debuggen
 * die falsche.
 *
 * Bedienung: Typ wählen → Attributabfrage → Treffer als Knoten. **Doppelklick klappt einen Knoten
 * aus** (aus- UND eingehende Kanten), Einfachklick zeigt alle Attribute rechts.
 *
 * **Die ganze Kette ist auf ihren Jahrgang festgenagelt, und das ist kein Zufall.** Das Portal fährt
 * **three 0.132** (August 2021) für die Gebäudeansichten. Die aktuellen Fassungen der Graph-Pakete
 * verlangen ein modernes three und bringen sich sonst eine ZWEITE Kopie mit — erzeugt wird dann mit
 * der einen Instanz und gerendert mit der anderen (`material.onBeforeRender is not a function`),
 * oder es fehlen schlicht Symbole (`three/webgpu`, `SRGBColorSpace`). Deshalb sind
 * `react-force-graph-3d` (1.21.10), `3d-force-graph` (1.70.5), `three-render-objects` (1.26.4) und
 * `three-forcegraph` (1.39.1) gepinnt — alle vom Sommer 2021, alle gegen dieses three gebaut.
 *
 * **Ein `^`-Bereich genügt hier nicht:** jede der drei Ebenen deklariert ihre Nachbarn mit Caret und
 * holt sich sonst die neueste — der Fehler taucht dann eine Ebene tiefer wieder auf. Wer die
 * Bibliothek aktualisieren will, aktualisiert three im ganzen Portal mit; das ist ein Sprung über
 * gut fünfzig Versionen und betrifft die Gebäudeansichten, nicht diesen Explorer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ForceGraph3D from "react-force-graph-3d";
import { CanvasTexture, Group, LinearFilter, Sprite, SpriteMaterial, Vector2 } from "three";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { Check, ChevronDown, FileText, Loader2, Moon, Network, Rocket, Search, Square, Sun, X } from "lucide-react";
import { cn } from "../lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  GraphTypeDescription,
  GraphTypeSummary,
  useGraphExplorerAPI,
} from "../GraphExplorerAPI";
import {
  buildJourneyPath,
  clusterPoints,
  distance,
  medianNearestNeighbourDistance,
  nearestPoint,
  dampVec,
  cockpitLookAt,
  pathPositionAt,
  pushOutside,
  shouldSwitchHighlight,
  resampleByArcLength,
  smoothPath,
  type Cluster,
  type JourneyPoint,
} from "./journeyGeometry";
import {
  ExplorerGraph,
  ExplorerNode,
  colorForRelation,
  colorForType,
  documentBadge,
  linkLabelPositions,
  highlightColorForType,
  emptyGraph,
  formatAttributeValue,
  isDoubleClick,
  markExpanded,
  mergeGraph,
  orderedAttributes,
  rawIdOfNodeId,
  relationLegend,
  typeLegend,
  typeMatchScore,
  dimmed,
  GRAPH_BACKGROUND,
  type GraphTheme,
  typeCaption,
  typeOfNodeId,
} from "./graphExplorerModel";

const PANEL_HEIGHT = "calc(100vh - 220px)";

/**
 * Stützstellen je **Sekunde**.
 *
 * Nicht je Bild: bei schwankender Bildrate wäre die Geschwindigkeit sonst schwankend, und genau
 * das fühlt sich als Ruckeln an — der Flug wird schneller, wenn der Rechner Luft hat, und
 * langsamer, wenn er es nicht hat. Mit 7,2 Stützstellen je Sekunde dauert eine Runde über 240
 * Stellen gut eine halbe Minute, unabhängig davon, wie schnell gezeichnet wird.
 */
const JOURNEY_SPEED_PER_SECOND = 7.2;

/**
 * Eine Textfahne für eine Kante.
 *
 * three bringt keine Textdarstellung mit; ein Sprite mit einer gezeichneten Leinwand ist der Weg
 * ohne zusätzliche Abhängigkeit (und `three-spritetext` wäre eine weitere, die an dieselbe
 * festgenagelte three-Version gebunden werden müsste). Die Fahne steht **immer zur Kamera**, das
 * ist die Eigenschaft eines Sprites — eine gedrehte Beschriftung im Raum liest niemand.
 */
// Rückgabetyp `any`: three 0.132 liefert keine Typdeklarationen, `@types/three` gibt es im Portal
// nicht — die Importe taugen als Werte, aber nicht als Typ.
/**
 * Gezeichnete Beschriftungen werden **wiederverwendet**.
 *
 * Ein Blatt an dreihundert Knoten heißt dreihundert Leinwände und dreihundert Texturen, wenn man
 * je Knoten neu zeichnet — dabei gibt es nur eine Handvoll verschiedener Texte („📄 1", „📄 2", …).
 * Das Material wird deshalb je Text gehalten; das Sprite selbst muss je Knoten eigen sein, weil es
 * seine Position im Bild trägt.
 */
const spriteMaterials = new Map<string, { material: any; width: number; height: number }>();

const makeLinkLabel = (text: string, color: string, scaleFactor = 0.075, theme: GraphTheme = "dark"): any => {
  const cacheKey = `${text}|${color}|${theme}`;
  const cached = spriteMaterials.get(cacheKey);
  if (cached) {
    const reused = new Sprite(cached.material);
    reused.scale.set(cached.width * scaleFactor, cached.height * scaleFactor, 1);
    return reused;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  const fontSize = 48;
  if (context) {
    context.font = `${fontSize}px sans-serif`;
    canvas.width = Math.ceil(context.measureText(text).width) + 24;
    canvas.height = fontSize + 20;
    const inner = canvas.getContext("2d")!;
    inner.font = `${fontSize}px sans-serif`;
    // Die Fahne hebt sich vom Grund ab: dunkel auf Dunkel, hell auf Hell.
    inner.fillStyle = theme === "light" ? "rgba(255,255,255,0.88)" : "rgba(0,0,0,0.65)";
    inner.fillRect(0, 0, canvas.width, canvas.height);
    inner.fillStyle = color;
    inner.textBaseline = "middle";
    inner.fillText(text, 12, canvas.height / 2);
  }
  const texture = new CanvasTexture(canvas);
  texture.minFilter = LinearFilter;
  const material = new SpriteMaterial({ map: texture, depthWrite: false, transparent: true });
  spriteMaterials.set(cacheKey, { material, width: canvas.width, height: canvas.height });
  const sprite = new Sprite(material);
  // Die Leinwand wird mit 48 px gezeichnet, damit die Schrift scharf ist; im Raum darf sie das
  // nicht sein — mit 0.18 überdeckte eine Fahne halbe Ballungen. Das Dokumentsymbol ist die
  // Ausnahme: es soll auffallen, also bekommt es seine eigene, größere Skalierung.
  const scale = scaleFactor;
  sprite.scale.set(canvas.width * scale, canvas.height * scale, 1);
  return sprite;
};

/** Wo der gewählte Grund im Browser liegt — eine Vorliebe des Betrachters, nichts Geteiltes. */
const THEME_STORAGE_KEY = "graphExplorer.theme";

const storedTheme = (): GraphTheme => {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
};

export default function GraphExplorerPanel() {
  const api = useGraphExplorerAPI();
  const [theme, setTheme] = useState<GraphTheme>(storedTheme);
  const light = theme === "light";
  /** Die Tafeln über dem Bild (Zähler, Legende) — halbtransparent in der Farbe des Grunds. */
  const overlay = light ? "border bg-white/85 text-slate-700 shadow-sm" : "bg-black/60 text-white/80";
  const toggleTheme = () => {
    const next: GraphTheme = light ? "dark" : "light";
    setTheme(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* ohne Speicher gilt der Grund nur bis zum Neuladen */
    }
  };
  const graphRef = useRef<any>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  /** Cache of link labels to avoid recreating textures. Key: `${source}-${target}-${relation}` */
  const linkLabelCache = useRef<Map<string, any>>(new Map());
  /**
   * Die gemessene Größe der Zeichenfläche.
   *
   * **Ohne sie sieht man nichts.** `react-force-graph` misst sich ohne `width`/`height` am
   * FENSTER, nicht am Elternelement: die Leinwand wird größer als ihr Container, die
   * Kraftsimulation zentriert die Knoten in deren Mitte — und die liegt hinter dem
   * `overflow-hidden` des Containers. Das Bild ist dann leer, obwohl Daten da sind.
   */
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const [types, setTypes] = useState<GraphTypeSummary[]>([]);
  /**
   * Typname → deutsche Beschriftung, aus dem Modell.
   *
   * Die Typenliste holt sie ohnehin für das Auswahlfeld; damit sind sie überall verfügbar, ohne
   * dass der Server sie an jedem Knoten mitschicken müsste.
   */
  const typeLabels = useMemo(
    () => new Map(types.map(type => [type.name, type.label])),
    [types],
  );
  const [selectedType, setSelectedType] = useState<string>("");
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [query, setQuery] = useState<string>("");
  const [graph, setGraph] = useState<ExplorerGraph>(emptyGraph());
  const [selected, setSelected] = useState<ExplorerNode | null>(null);
  const [typeDescription, setTypeDescription] = useState<GraphTypeDescription | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [problems, setProblems] = useState<string[]>([]);

  useEffect(() => {
    api
      .listTypes()
      .then(result => setTypes(result.types))
      .catch(error => setMessage(String(error?.message ?? error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Die Typbeschreibung des ausgewählten Knotens — sie ordnet die Attribute im Seitenpanel. */
  useEffect(() => {
    if (!selected) return;
    const type = typeOfNodeId(selected.id);
    if (typeDescription?.type === type) return;
    let cancelled = false;
    api
      .describeType(type)
      .then(result => {
        if (!cancelled) setTypeDescription(result);
      })
      .catch(() => {
        if (!cancelled) setTypeDescription(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    const element = canvasRef.current;
    if (!element) return;
    const measure = () =>
      setCanvasSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
    // Der Container entsteht erst, wenn Knoten da sind — deshalb hängt die Messung an der
    // Knotenzahl und nicht nur am ersten Rendern.
  }, [graph.nodes.length, selected]);

  const runSearch = useCallback(async () => {
    if (!selectedType) return;
    setBusy(true);
    setMessage("");
    setProblems([]);
    try {
      const result = await api.search(selectedType, query);
      setProblems(result.problems ?? []);
      // Eine Suche ERSETZT die Karte. Sie an eine vorherige anzuhängen ergäbe ein Bild, in dem
      // niemand mehr sagen kann, welcher Knoten woher kam.
      setGraph({
        nodes: result.nodes.map(node => ({ ...node })),
        links: [],
      });
      setSelected(null);
      setMessage(
        result.nodes.length
          ? `${result.nodes.length} von ${result.total}${result.capped ? "+" : ""} Objekten`
          : "Keine Treffer.",
      );
    } catch (error: any) {
      setMessage(String(error?.message ?? error));
    } finally {
      setBusy(false);
    }
  }, [api, selectedType, query]);

  const expandNode = useCallback(
    async (node: ExplorerNode) => {
      setBusy(true);
      try {
        const expansion = await api.expand(typeOfNodeId(node.id), rawIdOfNodeId(node.id));
        setGraph(current =>
          markExpanded(
            mergeGraph(current, {
              nodes: expansion.nodes.map(n => ({ ...n })),
              links: expansion.edges.map(edge => ({ ...edge })),
            }),
            node.id,
          ),
        );
        setMessage(
          expansion.nodes.length
            ? `${expansion.nodes.length} Nachbar(n) über ${expansion.edges.length} Kante(n)` +
                (expansion.truncated.length
                  ? ` — gekürzt: ${expansion.truncated
                      .map(t => `${t.relation} ${t.shown}/${t.total}`)
                      .join(", ")}`
                  : "")
            : "Keine Nachbarn.",
        );
      } catch (error: any) {
        setMessage(String(error?.message ?? error));
      } finally {
        setBusy(false);
      }
    },
    [api],
  );

  /**
   * Einfachklick wählt aus, Doppelklick klappt aus.
   *
   * Die Bibliothek liefert nur `onNodeClick`, also wird der Doppelklick hier erkannt: zweimal
   * derselbe Knoten innerhalb von 350 ms. Die Auswahl passiert schon beim ersten Klick — sie
   * hinauszuzögern, um auf einen zweiten zu warten, machte die Bedienung träge, und die Auswahl
   * ist folgenlos, das Ausklappen nicht.
   */
  const lastClick = useRef<{ id: string; at: number }>({ id: "", at: 0 });

  const handleNodeClick = useCallback(
    (node: ExplorerNode) => {
      const now = Date.now();
      const second = isDoubleClick(lastClick.current, node.id, now);
      lastClick.current = { id: node.id, at: now };
      setSelected(node);
      if (second && !busy) {
        lastClick.current = { id: "", at: 0 };
        expandNode(node);
      }
    },
    [expandNode, busy],
  );

  /**
   * **Die Reise.**
   *
   * Zwei Schritte, und der erste ist der langweilige: die zehn Ebenen holen und die Simulation
   * ausrechnen lassen. Erst wenn sich die Wolke **materialisiert** hat, steht die Information im
   * Raum — dichte Ballungen sind zusammenhängende Teilgraphen, Ausreißer hängen am Rand. Der Flug
   * hängt deshalb an der Geometrie und nicht am Graphen: den Kanten zu folgen hieße, Zickzack durch
   * dieselbe Wolke zu fliegen.
   */
  /**
   * Der aktuelle Graph als Ref.
   *
   * Die Positionen stehen **in unseren eigenen Knotenobjekten**: die Bibliothek schreibt `x/y/z`
   * hinein (deshalb werden sie nicht kopiert, siehe `graphData`). Ein `instance.graphData()` gibt
   * es in dieser Fassung NICHT — der Aufruf lieferte `undefined`, die Reise fand null Punkte und
   * stieg stumm aus. Genau so sah es aus: „ich klicke Reise, nichts passiert."
   */
  const latestGraphRef = useRef<ExplorerGraph>(emptyGraph());
  useEffect(() => {
    latestGraphRef.current = graph;
  }, [graph]);

  const [journeyActive, setJourneyActive] = useState(false);
  /**
   * Ob gerade eine Reise geholt wird.
   *
   * `busy` allein genügt nicht: Es sperrt jede Bedienung, sagt aber nicht, WORAUF gewartet wird —
   * der Ladekringel erschien deshalb am Suchknopf, obwohl niemand gesucht hatte. Die Anzeige gehört
   * an die Schaltfläche, die den Vorgang ausgelöst hat.
   */
  const [journeyLoading, setJourneyLoading] = useState(false);
  /**
   * Während der Reise ruht die **Simulation**, nicht die Darstellung.
   *
   * `pauseAnimation()` friert die Zeichenfläche ein — die Kamera bewegte sich dann zwar, aber
   * niemand sähe es. `cooldownTicks: 0` hält nur das Kräftespiel an, und genau darum geht es: die
   * Bahn ist auf DIESE Positionen gerechnet.
   */
  const [simulationFrozen, setSimulationFrozen] = useState(false);
  const [journeyLabel, setJourneyLabel] = useState<string>("");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  /** Der gehaltene Knoten samt Zeitpunkt — die Grundlage der Hysterese. */
  const highlightRef = useRef<{ id: string; distance: number; since: number } | null>(null);
  /** Die Unterlagen des gerade betrachteten Knotens — Grundlage für das Symbol. */
  const [highlightDocuments, setHighlightDocuments] = useState({ total: 0, primary: 0, reference: 0 });
  /** Die zuletzt gesetzten Unterlagenzahlen — Grundlage für den Same-Value-Bail-Out. */
  const highlightDocumentsRef = useRef({ total: 0, primary: 0, reference: 0 });
  const flightRef = useRef<{
    path: any[];
    /** Gebrochener Fortschritt auf der Bahn — das Tempo ist eine Zahl, keine Frage der Bildrate. */
    progress: number;
    clusters: Cluster[];
    points: JourneyPoint[];
  } | null>(null);
  /** Der ausstehende Start — beim Stoppen abgebrochen, sonst hebt die Reise 2,5 s später doch ab. */
  const journeyTimerRef = useRef<number | null>(null);

  const stopJourney = useCallback(() => {
    if (journeyTimerRef.current !== null) {
      clearTimeout(journeyTimerRef.current);
      journeyTimerRef.current = null;
    }
    flightRef.current = null;
    setJourneyActive(false);
    setJourneyLabel("");
    setHighlightId(null);
    highlightRef.current = null;
    highlightDocumentsRef.current = { total: 0, primary: 0, reference: 0 };
    setHighlightDocuments({ total: 0, primary: 0, reference: 0 });
    setSimulationFrozen(false);
    // Zusätzlich zum Zustand die Instanz selbst: ein `cooldownTicks`, das auf `undefined`
    // zurückfällt, weckt das Kräftespiel nicht wieder auf — der Graph bliebe für immer starr.
    graphRef.current?.cooldownTicks?.(Infinity);
  }, []);

  const startJourney = useCallback(
    async (node: ExplorerNode) => {
      setBusy(true);
      setJourneyLoading(true);
      try {
        const journey = await api.journey(typeOfNodeId(node.id), rawIdOfNodeId(node.id));
        setGraph({
          nodes: journey.nodes.map(n => ({ ...n })),
          links: journey.edges.map(edge => ({ ...edge })),
        });
        setMessage(
          `Reise: ${journey.nodes.length} Objekte über ${journey.depth} Ebenen` +
            (journey.truncated ? ` — ${journey.truncated}` : ""),
        );
        // Der Wolke Zeit zum Auspendeln geben: eine Bahn, die auf wandernde Punkte gerechnet ist,
        // jagt ein Ziel, das wegläuft.
        journeyTimerRef.current = window.setTimeout(() => {
          try {
          const instance = graphRef.current;
          if (!instance) {
            setMessage('Die Zeichenfläche ist nicht bereit — Reise abgebrochen.');
            return;
          }
          const live: JourneyPoint[] = latestGraphRef.current.nodes
            .filter((n: any) => Number.isFinite(n.x) && Number.isFinite(n.y) && Number.isFinite(n.z))
            .map((n: any) => ({ id: n.id, type: n.type, label: n.label, x: n.x, y: n.y, z: n.z }));
          if (live.length < 3) {
            // Nie stumm aussteigen: „nichts passiert" ist die schlechteste Rückmeldung, weil sie
            // nicht sagt, ob die Daten fehlen oder der Flug nicht startet.
            setMessage(
              `Reise nicht möglich: erst ${live.length} von ${latestGraphRef.current.nodes.length} Objekten haben eine Position. Nochmal versuchen, wenn sich die Wolke gelegt hat.`,
            );
            return;
          }
          const clusters = clusterPoints(live);
          const raw = buildJourneyPath(live, clusters);
          const scale = medianNearestNeighbourDistance(live);
          // Close the loop: append the first waypoint so the closing segment also goes through
          // pushOutside. Without this, the camera can fly straight through the cloud from the
          // last cluster orbit back to the first grand-tour waypoint.
          const rawClosed = [...raw, raw[0]];
          const safeClosed = pushOutside(rawClosed, live, scale * 2.5);
          const smoothClosed = smoothPath(safeClosed.map(w => w.position), 6);
          // Apply pushOutside again after smoothing: Catmull-Rom interpolation can create
          // points that pass through the cloud even when the discrete waypoints are outside.
          const smoothWaypoints = smoothClosed.map(position => ({ position, focus: null, cluster: null }));
          const smoothSafe = pushOutside(smoothWaypoints, live, scale * 2.5);
          // **Nach dem Herausdrücken noch einmal glätten.** `pushOutside` schiebt jede Stützstelle
          // einzeln und radial von dem Knoten weg, der ihr zu nahe kam — benachbarte Stützstellen
          // treffen dabei verschiedene Knoten und werden in verschiedene Richtungen versetzt. Was
          // vorher eine runde Kurve war, bekommt so Ecken, und eine Ecke ist im Flug ein Ruck.
          const rounded = smoothPath(smoothSafe.map(w => w.position), 3);
          // Remove the duplicate endpoint before arc-length resampling: the wrapping in
          // pathPositionAt will close the loop, and keeping the duplicate would make the last
          // segment twice as dense.
          const smooth = rounded.slice(0, -1);
          const safe = safeClosed.slice(0, -1);
          // **Die Zieldichte hängt an der rohen Bahn, nicht am Glättungsergebnis.** Das Tempo ist in
          // Stützstellen je Sekunde angegeben; würde hier `smooth.length` stehen, machte jede
          // zusätzliche Glättungsstufe den Flug entsprechend länger — die zweite Runde gegen die
          // Ecken hätte ihn verdreifacht.
          const even = resampleByArcLength(smooth, Math.max(safe.length * 6, 240));
          
          // Kumulative Bogenlängen für beide Bahnen berechnen, um Fokus nach Entfernung zu mappen.
          const safeCumulative = [0];
          for (let i = 1; i < safe.length; i++) {
            safeCumulative.push(safeCumulative[i - 1] + distance(safe[i - 1].position, safe[i].position));
          }
          const safeTotal = safeCumulative[safeCumulative.length - 1];
          
          const evenCumulative = [0];
          for (let i = 1; i < even.length; i++) {
            evenCumulative.push(evenCumulative[i - 1] + distance(even[i - 1], even[i]));
          }
          const evenTotal = evenCumulative[evenCumulative.length - 1];
          
          // Der Fokus wird aus den Wegpunkten mitgeführt: beim Umrunden schaut die Kamera auf den
          // Schwerpunkt der Ballung, im Transit nach vorn. Das ist der Unterschied zwischen
          // „vorbeifliegen" und „etwas zeigen".
          const focusPerSample = even.map((_, index) => {
            if (safeTotal < 1e-9) {
              return { focus: safe[0]?.focus ?? null, cluster: safe[0]?.cluster ?? null };
            }
            const targetArcLength = (evenCumulative[index] / evenTotal) * safeTotal;
            let cursor = 0;
            while (cursor < safeCumulative.length - 2 && safeCumulative[cursor + 1] < targetArcLength) {
              cursor++;
            }
            const source = safe[cursor];
            return { focus: source?.focus ?? null, cluster: source?.cluster ?? null };
          });
          
          flightRef.current = {
            path: even.map((position, index) => ({ position, ...focusPerSample[index] })),
            progress: 0,
            clusters,
            points: live,
          };
          // Die Simulation anhalten: die Bahn ist auf DIESE Positionen gerechnet.
          setSimulationFrozen(true);
          instance.cooldownTicks?.(0);
          setJourneyActive(true);
          journeyTimerRef.current = null;
          setMessage(`Reise: ${clusters.length} Ballung(en), ${even.length} Bahnpunkte.`);
          } catch (error: any) {
            // Ein Fehler in einem Timeout landet sonst nirgends — der Knopf sähe kaputt aus.
            setMessage(`Reise fehlgeschlagen: ${String(error?.message ?? error)}`);
          }
        }, 2500);
      } catch (error: any) {
        setMessage(String(error?.message ?? error));
      } finally {
        setBusy(false);
        setJourneyLoading(false);
      }
    },
    [api],
  );

  /** Den Timer beim Unmount aufräumen. */
  useEffect(() => {
    return () => {
      if (journeyTimerRef.current !== null) {
        clearTimeout(journeyTimerRef.current);
      }
    };
  }, []);

  /** Clear link label cache on graph changes. Materials are not disposed as they are shared. */
  useEffect(() => {
    return () => {
      linkLabelCache.current.clear();
    };
  }, [graph.nodes.length, graph.links.length]);

  /** Der Flug: ein Schritt je Bild, Kamera auf die Bahn, Blick auf Ballung oder voraus. */
  useEffect(() => {
    if (!journeyActive) return;
    let cancelled = false;
    let lastFrame = 0;
    let smoothedLook: { x: number; y: number; z: number } | null = null;
    const tick = (timestamp: number) => {
      if (cancelled) return;
      const flight = flightRef.current;
      const instance = graphRef.current;
      if (!flight || !instance) return;
      // Ein ausgelassenes Bild (Tab im Hintergrund, Nachladen) darf keinen Sprung machen: die
      // verstrichene Zeit wird gedeckelt.
      const elapsed = lastFrame ? Math.min(timestamp - lastFrame, 100) : 16;
      lastFrame = timestamp;
      const current = pathPositionAt(flight.path, flight.progress);
      // Der Blick geht ein Stück voraus, nicht auf die nächste Stützstelle: sonst zeigt die Kamera
      // im Transit auf einen Punkt, den sie im nächsten Bild schon erreicht hat.
      const ahead = pathPositionAt(flight.path, flight.progress + 6);
      // Der Schwerpunkt zeigt die Ballung, die Flugrichtung zeigt, dass man fliegt. Gemischt —
      // und wenn der Blick hinter die Bewegung fiele, gewinnt die Bewegung: sonst sieht es aus,
      // als flöge man rückwärts.
      const target = cockpitLookAt(current.position, ahead.position, current.focus);
      // **Der Blickpunkt wird nachgezogen, nicht gesetzt.** Er springt sonst dreimal: beim Wechsel
      // der Ballung, beim Umschalten zwischen „auf die Ballung" und „nach vorn", und an jeder
      // Stützstelle. Jeder dieser Sprünge ist ein Ruck.
      smoothedLook = smoothedLook ? dampVec(smoothedLook, target, elapsed) : target;
      instance.cameraPosition(current.position, smoothedLook, 0);

      // Der räumlich nächste Knoten wird hervorgehoben — das ist das, worauf man gerade schaut.
      //
      // **Mit Hysterese**, und Beschriftung und Vergrößerung wechseln GEMEINSAM: ohne sie springt
      // der nächste Knoten mehrmals je Sekunde, der Text flackert, und weil die Kugel ihre Größe
      // träger annimmt, gehören beide sichtbar nicht mehr zusammen.
      const near = nearestPoint(current.position, flight.points);
      if (near) {
        const now = Date.now();
        const held = highlightRef.current;
        if (shouldSwitchHighlight(held, near.point.id, near.distance, now - (held?.since ?? 0))) {
          highlightRef.current = { id: near.point.id, distance: near.distance, since: now };
          setHighlightId(near.point.id);
          const cluster = current.cluster !== null ? flight.clusters[current.cluster] : null;
          // Nur die BALLUNG steht oben. Typ und Name des betrachteten Knotens gehören an den
          // Knoten selbst — eine Beschriftung am Bildrand zwingt den Blick weg von dem, was sie
          // beschreibt, und bei einer fliegenden Kamera verliert man dann beides.
          setJourneyLabel(cluster ? `${cluster.label} · ${cluster.count}` : "");
          const node = latestGraphRef.current.nodes.find(n => n.id === near.point.id);
          const badge = documentBadge(node?.attributes);
          highlightDocumentsRef.current = badge;
          setHighlightDocuments(badge);
        } else if (held && held.id === near.point.id) {
          held.distance = near.distance;
          const node = latestGraphRef.current.nodes.find(n => n.id === near.point.id);
          const badge = documentBadge(node?.attributes);
          if (
            badge.total !== highlightDocumentsRef.current.total ||
            badge.reference !== highlightDocumentsRef.current.reference
          ) {
            highlightDocumentsRef.current = badge;
            setHighlightDocuments(badge);
          }
        }
      }

      // Ein Bruchteil je Bild statt einer ganzen Stützstelle — und die Reise läuft **um**, bis
      // jemand sie beendet. Ein Flug, der von selbst aufhört, reißt genau dann ab, wenn man noch
      // hinsieht.
      flight.progress += (JOURNEY_SPEED_PER_SECOND * elapsed) / 1000;
      window.requestAnimationFrame(tick);
    };
    const handle = window.requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(handle);
    };
  }, [journeyActive, stopJourney]);

  const attributeRows = useMemo(() => {
    if (!selected) return [];
    // Die Felder samt Beschriftung, nicht nur ihre Reihenfolge — das Modell führt sie zweisprachig.
    const fields = (typeDescription?.fields ?? []).map(field => ({
      name: field.name,
      label: field.label,
    }));
    return orderedAttributes(selected.attributes ?? {}, fields);
  }, [selected, typeDescription]);

  /**
   * Das Leuchten (Bloom).
   *
   * `3d-force-graph` reicht seinen Nachbearbeitungs-Composer heraus; dort hängt der Pass **einmal**
   * hinein — ein zweiter bei jedem Rendern summierte sich zu einem weißen Bild. Die Werte sind
   * bewusst zurückhaltend: ein starkes Bloom sieht auf einem Bildschirmfoto gut aus und macht
   * überlappende Knoten ununterscheidbar, und genau die will man beim Debuggen auseinanderhalten.
   */
  const bloomRef = useRef<any>(null);
  useEffect(() => {
    if (!graph.nodes.length) {
      bloomRef.current = null;
      return;
    }
    const instance = graphRef.current;
    if (!instance?.postProcessingComposer || bloomRef.current) return;
    try {
      const composer = instance.postProcessingComposer();
      if (!composer) return;
      // Ein Hauch, kein Scheinwerfer: Stärke 0.25, enger Radius und eine hohe Schwelle (0.6), also
      // glüht nur, was ohnehin hell ist. Die Ansicht dient dem Debuggen — benachbarte Knoten
      // auseinanderzuhalten schlägt jeden Effekt, und ein kräftiges Bloom verschmiert sie zu einem
      // Fleck. Zweimal nachgeschärft, beide Male nach unten.
      const bloom = new UnrealBloomPass(
        new Vector2(canvasSize.width || 800, canvasSize.height || 600),
        0.25,
        0.3,
        0.6,
      );
      composer.addPass(bloom);
      bloomRef.current = bloom;
    } catch (error) {
      // Ohne Leuchten ist der Explorer vollständig benutzbar — ein fehlgeschlagener Effekt darf
      // die Ansicht nicht kosten.
      console.warn("Bloom konnte nicht eingerichtet werden", error);
    }
  }, [graph.nodes.length, canvasSize.width, canvasSize.height]);

  // Auf hellem Grund überstrahlt das Leuchten alles zu Weiß — dort ist es aus.
  useEffect(() => {
    if (bloomRef.current) bloomRef.current.enabled = !light;
  });

  // Die Kantenfahnen sind je Kante zwischengespeichert — mit dem Grund wechselt ihre Farbe.
  useEffect(() => {
    linkLabelCache.current.clear();
  }, [theme]);

  const legend = useMemo(() => relationLegend(graph.links, theme), [graph.links, theme]);
  const typeEntries = useMemo(() => typeLegend(graph.nodes, theme), [graph.nodes, theme]);

  /** Über welchem Legendeneintrag die Maus steht — `null`, solange man nichts meint. */
  const [hovered, setHovered] = useState<{ kind: "type" | "relation"; value: string } | null>(null);

  /**
   * Wer beim Überfahren gemeint ist.
   *
   * Die Kraftsimulation ersetzt `link.source`/`link.target` unterwegs durch die Knotenobjekte
   * selbst — dieselben Objekte, die auch in `graph.links` stehen. Deshalb muss beides gelesen
   * werden können, Id wie Objekt.
   */
  const meant = useMemo(() => {
    if (!hovered) return null;
    const endId = (end: any): string => (end && typeof end === "object" ? String(end.id) : String(end));
    const nodes = new Set<string>();
    const links = new Set<string>();
    if (hovered.kind === "type") {
      for (const node of graph.nodes) if (node.type === hovered.value) nodes.add(node.id);
      // Eine Kante gehört dazu, wenn sie den Typ berührt — sonst hängen die hellen Kugeln im Leeren.
      for (const link of graph.links)
        if (nodes.has(endId(link.source)) || nodes.has(endId(link.target))) links.add(link.id);
    } else {
      for (const link of graph.links)
        if (link.relation === hovered.value) {
          links.add(link.id);
          nodes.add(endId(link.source));
          nodes.add(endId(link.target));
        }
    }
    return { nodes, links };
  }, [hovered, graph.nodes, graph.links]);
  /** Wo die Fahne je Kante sitzt — mehrere Kanten desselben Paares verteilen sich um die Mitte. */
  const labelPositions = useMemo(() => linkLabelPositions(graph.links), [graph.links]);

  /**
   * Nach jeder Änderung die Kamera auf den Bestand setzen.
   *
   * Die Simulation braucht einen Moment, bis die Knoten ihre Plätze haben; `zoomToFit` sofort nach
   * dem Laden rahmt einen Haufen im Ursprung. Deshalb mit kurzer Verzögerung — und nur, wenn es
   * überhaupt etwas zu rahmen gibt.
   */
  useEffect(() => {
    if (!graph.nodes.length || !graphRef.current) return;
    const timer = setTimeout(() => graphRef.current?.zoomToFit?.(600, 60), 600);
    return () => clearTimeout(timer);
  }, [graph.nodes.length, graph.links.length]);

  /**
   * Die Objekte werden **nicht** kopiert.
   *
   * `react-force-graph` schreibt Position und Geschwindigkeit in die Knotenobjekte selbst. Bei
   * jedem Rendern frische Kopien zu übergeben hieße, ihnen bei jedem Ausklappen ihre Plätze zu
   * nehmen: die Simulation begänne von vorn und das Bild zuckt. `mergeGraph` erhält die Identität
   * der bekannten Knoten, genau dafür.
   */
  const graphData = useMemo(() => ({ nodes: graph.nodes, links: graph.links }), [graph]);

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Kein natives `<select>` mehr: Die Liste ist lang, und ein Betriebssystem-Menü lässt sich
            weder durchsuchen noch in die Gestaltung des Portals einfügen. Gefiltert wird über
            beides — die deutsche Beschriftung und den englischen Klassennamen —, weil man mal das
            eine und mal das andere im Kopf hat. */}
        <Popover open={typePickerOpen} onOpenChange={setTypePickerOpen}>
          <PopoverTrigger
            data-testid="graph-type-select"
            role="combobox"
            aria-expanded={typePickerOpen}
            disabled={busy}
            className="flex h-9 w-[280px] max-w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm outline-none transition-colors focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={cn("line-clamp-1 text-left", !selectedType && "text-muted-foreground")}>
              {selectedType
                ? `${typeCaption(selectedType, typeLabels).label} (${selectedType})`
                : "Typ wählen …"}
            </span>
            <ChevronDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command filter={typeMatchScore}>
              <CommandInput placeholder="Klasse suchen …" />
              <CommandList>
                <CommandEmpty>Keine Klasse gefunden</CommandEmpty>
                <CommandGroup>
                  {types.map(type => (
                    <CommandItem
                      key={type.name}
                      value={`${type.label} ${type.name}`}
                      disabled={type.abstract}
                      onSelect={() => {
                        setSelectedType(type.name);
                        setTypePickerOpen(false);
                      }}
                    >
                      <Check
                        aria-hidden="true"
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          type.name === selectedType ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="flex-1 truncate">
                        {type.label}
                        <span className="ml-1.5 text-xs text-muted-foreground">({type.name})</span>
                      </span>
                      {type.abstract && (
                        <span className="ml-2 shrink-0 text-xs text-muted-foreground">abstrakt</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <input
          data-testid="graph-query-input"
          className="min-w-[320px] flex-1 rounded-md border bg-background px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          placeholder={'Attributabfrage, z. B. „city eq Frankfurt am Main“, „rent gt 1000, endDate missing“ — oder einfach ein Suchwort'}
          value={query}
          disabled={busy}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => event.key === "Enter" && !busy && runSearch()}
        />

        <button
          data-testid="graph-search"
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm"
          onClick={runSearch}
          disabled={!selectedType || busy}
        >
          {/* Der Kringel steht nur für die eigene Arbeit. Lädt die Reise, ist der Knopf gesperrt,
              aber still — sonst behauptet er, er suche gerade. */}
          {busy && !journeyLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
          Suchen
        </button>

        <button
          type="button"
          data-testid="graph-theme-toggle"
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm"
          onClick={toggleTheme}
          aria-pressed={light}
          title={light ? "Auf dunklen Grund umschalten" : "Auf hellen Grund umschalten (für Präsentation und Druck)"}
        >
          {light ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          {light ? "Dunkel" : "Hell"}
        </button>

        {message && <span className="text-xs text-muted-foreground">{message}</span>}
      </div>

      {problems.length > 0 && (
        // Was der Parser nicht verstanden hat, steht hier — ein verschluckter Filter liefert zu
        // viel und sähe aus wie ein Treffer.
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
          {problems.map(problem => (
            <div key={problem}>{problem}</div>
          ))}
        </div>
      )}

      <div className="flex min-h-0 flex-1 gap-3">
        <div
          ref={canvasRef}
          className={cn("relative min-w-0 flex-1 overflow-hidden rounded-lg border", light ? "bg-slate-50" : "bg-black")}
          style={{ height: PANEL_HEIGHT }}
        >
          {graph.nodes.length > 0 && (
            // Damit „nichts zu sehen" von „nichts geladen" unterscheidbar ist.
            <div className={cn("pointer-events-none absolute left-2 top-2 z-10 rounded px-2 py-1 text-[11px]", overlay)}>
              {graph.nodes.length} Knoten · {graph.links.length} Kanten · {canvasSize.width}×
              {canvasSize.height}
            </div>
          )}
          {journeyActive && (
            // Was gerade im Bild ist: die Ballung und der räumlich nächste Knoten.
            journeyLabel && (
              // Nur die Ballung: „woraus besteht das hier, worüber ich gerade fliege". Typ, Name
              // und Unterlagen des Knotens stehen am Knoten.
              <div
                className={cn(
                  "pointer-events-none absolute left-1/2 top-4 z-10 flex -translate-x-1/2 items-center gap-2 rounded px-3 py-1.5 text-sm",
                  light ? "border bg-white/90 text-slate-900 shadow-sm" : "bg-black/70 text-white",
                )}
              >
                {journeyLabel}
                {highlightDocuments.total > 0 && (
                  <span
                    className={cn("inline-flex items-center gap-1 text-xs", light ? "text-slate-500" : "text-white/70")}
                    title={
                      `${highlightDocuments.primary} Unterlage(n) an diesem Objekt` +
                      (highlightDocuments.reference
                        ? `, ${highlightDocuments.reference} nur erwähnt`
                        : "")
                    }
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {/* Die Summe der beiden Quellen war einmal `eigene + gebundene`. Es gibt nur
                        noch eine Quelle; getrennt gezeigt wird jetzt, was dem Objekt gehört und
                        worin es nur vorkommt. */}
                    {highlightDocuments.primary}
                    {highlightDocuments.reference ? ` + ${highlightDocuments.reference}` : ""}
                  </span>
                )}
              </div>
            )
          )}
          {(typeEntries.length > 0 || legend.length > 0) && (
            // Welche Farbe was ist — sonst ist die Einfärbung Dekoration. Getrennt nach Knoten und
            // Kanten, weil sonst „Anlagen" (die Beziehung) und „Anlage" (die Klasse) nebeneinander
            // stehen und wie derselbe Begriff in zwei Numeri aussehen.
            <div
              className={cn("absolute bottom-2 left-2 z-10 max-w-[60%] rounded px-2 py-1.5 text-[11px]", overlay)}
              onMouseLeave={() => setHovered(null)}
            >
              {typeEntries.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={cn("uppercase tracking-wide", light ? "text-slate-400" : "text-white/40")}>Objekte</span>
                  {typeEntries.map(entry => (
                    <button
                      key={entry.type}
                      type="button"
                      className={cn("inline-flex items-center gap-1 rounded px-0.5", light ? "hover:bg-slate-900/5" : "hover:bg-white/10")}
                      onMouseEnter={() => setHovered({ kind: "type", value: entry.type })}
                      onFocus={() => setHovered({ kind: "type", value: entry.type })}
                      onBlur={() => setHovered(null)}
                    >
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />
                      {typeCaption(entry.type, typeLabels).label}
                      <span className={light ? "text-slate-500" : "text-white/50"}>·{entry.count}</span>
                      {/* Wo Unterlagen liegen, sieht man sonst erst, wenn man die Blätter an den
                          Knoten im Bild findet. Hier steht es je Klasse. */}
                      {entry.documents > 0 && (
                        <span
                          className={cn("inline-flex items-center gap-0.5", light ? "text-sky-700" : "text-sky-300/80")}
                          title={
                            `${entry.documents} Unterlage(n) an ${entry.withDocuments} von ` +
                            `${entry.count} Objekt(en) dieser Klasse`
                          }
                        >
                          <FileText className="h-3 w-3" />
                          {entry.documents}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {legend.length > 0 && (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={cn("uppercase tracking-wide", light ? "text-slate-400" : "text-white/40")}>Beziehungen</span>
                  {legend.map(entry => (
                    <button
                      key={entry.relation}
                      type="button"
                      className={cn("inline-flex items-center gap-1 rounded px-0.5", light ? "hover:bg-slate-900/5" : "hover:bg-white/10")}
                      onMouseEnter={() => setHovered({ kind: "relation", value: entry.relation })}
                      onFocus={() => setHovered({ kind: "relation", value: entry.relation })}
                      onBlur={() => setHovered(null)}
                    >
                      <span
                        className="inline-block h-2 w-4 rounded-sm"
                        style={{ backgroundColor: entry.color }}
                      />
                      {entry.label}
                      <span className={light ? "text-slate-500" : "text-white/50"}>·{entry.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {graph.nodes.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <Network className="mr-2 h-5 w-5" />
              Typ wählen und suchen — Doppelklick auf einen Knoten klappt sein Umfeld aus.
            </div>
          ) : (
            <ForceGraph3D
              ref={graphRef}
              width={canvasSize.width || undefined}
              height={canvasSize.height || undefined}
              graphData={graphData}
              cooldownTicks={simulationFrozen ? 0 : undefined}
              backgroundColor={GRAPH_BACKGROUND[theme]}
              nodeLabel={(node: any) => `${node.label}\n${node.type}`}
              nodeColor={(node: any) => {
                const eigen =
                  node.id === highlightId
                    ? highlightColorForType(node.type, theme)
                    : colorForType(node.type, theme);
                return meant && !meant.nodes.has(node.id) ? dimmed(eigen, theme) : eigen;
              }}
              nodeVal={(node: any) => (node.id === highlightId ? 6 : 1)}
              nodeOpacity={0.95}
              nodeRelSize={5}
              // Die Kugel ist ein Ikosaeder-Netz; bei der Voreinstellung (8) sieht man die
              // Dreiecke. 32 ist rund, ohne die Bildrate zu kosten — die Knoten sind klein und
              // ihre Zahl liegt im Dutzendbereich, nicht im Zehntausenderbereich.
              nodeResolution={32}
              // Farbe nach RELATION (die Richtung sagt der Pfeil), dazu spürbar breiter und
              // deckender: eine Linie muss sich gegen den dunklen Grund behaupten, erst recht
              // neben leuchtenden Kugeln.
              linkColor={(link: any) => {
                const eigen = colorForRelation(link.relation, theme);
                return meant && !meant.links.has(link.id) ? dimmed(eigen, theme) : eigen;
              }}
              linkWidth={(link: any) => (meant && meant.links.has(link.id) ? 3 : 1.2)}
              linkOpacity={light ? 0.7 : 0.55}
              linkDirectionalArrowLength={4.5}
              linkDirectionalArrowRelPos={1}
              linkDirectionalParticles={2}
              linkDirectionalParticleWidth={2}
              linkDirectionalParticleSpeed={0.006}
              linkLabel={(link: any) => `${link.label} (${link.direction === "in" ? "eingehend" : "ausgehend"})`}
              // Nur die Kanten AM betrachteten Knoten tragen eine Fahne. Alle zu beschriften wäre
              // eine Textwand: bei 900 Kanten liest man keine einzige davon.
              // Ein Blatt über dem betrachteten Knoten, wenn Unterlagen an ihm hängen. Nur dort:
              // an dreihundert Knoten wäre es Konfetti.
              nodeThreeObjectExtend={true}
              nodeThreeObject={(node: any) => {
                const badge = documentBadge(node.attributes);
                const focused = node.id === highlightId || node.id === selected?.id;
                if (!badge.total && !focused) return undefined as any;

                const type = typeOfNodeId(node.id);
                const group = new Group();

                if (badge.total) {
                  // Das Blatt hängt an JEDEM Knoten, der Unterlagen hat — man soll sehen, wo etwas
                  // liegt, ohne hinfliegen zu müssen. Am betrachteten Knoten größer und höher, sonst
                  // klein und dicht über der Kugel.
                  //
                  // **Blatt und Kette sagen Verschiedenes.** 📄 ist die Unterlage des Objekts, 🔗
                  // die bloße Erwähnung darin: Bei einem Mietvertragsimport trüge sonst jeder
                  // Beteiligte dasselbe Blatt, und „wo liegt der Vertrag" hätte sechs gleich
                  // aussehende Antworten. Wer nur erwähnt wird, bekommt außerdem eine
                  // zurückgenommene Farbe — er soll auffindbar sein, aber nicht mitleuchten.
                  const teile = [
                    badge.primary ? `📄 ${badge.primary}` : "",
                    badge.reference ? `🔗 ${badge.reference}` : "",
                  ].filter(Boolean);
                  const symbol = makeLinkLabel(
                    teile.join("  "),
                    badge.primary ? (light ? "#0f172a" : "#ffffff") : light ? "#64748b" : "#8fa3bf",
                    focused ? 0.16 : 0.085,
                    theme,
                  );
                  symbol.position.set(0, focused ? 17 : 8, 0);
                  group.add(symbol);
                }

                if (focused) {
                  // Beschriftung UND technischer Name: gelesen wird „Mietvertrag", getippt `Lease`.
                  const naming = typeCaption(type, typeLabels);
                  const label =
                    naming.label === naming.technical
                      ? naming.label
                      : `${naming.label} (${naming.technical})`;
                  const caption = makeLinkLabel(
                    `${label} · ${node.label}`,
                    highlightColorForType(type, theme),
                    undefined,
                    theme,
                  );
                  // Über der Kugel, nicht auf ihr: sonst verdeckt die Fahne, was sie beschriftet.
                  caption.position.set(0, 10, 0);
                  group.add(caption);
                }
                return group;
              }}
              linkThreeObjectExtend={true}
              linkThreeObject={(link: any) => {
                const focus = highlightId ?? (selected ? selected.id : null);
                if (!focus) return undefined as any;
                const source = typeof link.source === "object" ? link.source.id : link.source;
                const target = typeof link.target === "object" ? link.target.id : link.target;
                if (source !== focus && target !== focus) return undefined as any;
                const key = `${source}-${target}-${link.relation}-${theme}`;
                let sprite = linkLabelCache.current.get(key);
                if (!sprite) {
                  sprite = makeLinkLabel(
                    link.label || link.relation,
                    colorForRelation(link.relation, theme),
                    undefined,
                    theme,
                  );
                  linkLabelCache.current.set(key, sprite);
                }
                return sprite;
              }}
              linkPositionUpdate={(sprite: any, { start, end }: any, link: any) => {
                if (!sprite) return false;
                // Nicht stur in die Mitte: zwischen zwei Knoten liegen oft mehrere Kanten (eine
                // beidseitige Beziehung erscheint hin und zurück), und übereinanderliegende Fahnen
                // liest niemand. Der Anteil kommt aus `linkLabelPositions`.
                const t = labelPositions[link?.id] ?? 0.5;
                sprite.position.set(
                  start.x + (end.x - start.x) * t,
                  start.y + (end.y - start.y) * t,
                  start.z + (end.z - start.z) * t,
                );
                return false;
              }}
              // `react-force-graph-3d` kennt keinen Doppelklick — er wird hier erkannt (siehe
              // `handleNodeClick`). Der Rechtsklick klappt ebenfalls aus, weil ein Doppelklick in
              // einer 3D-Szene auch als Zoomgeste gelesen werden kann.
              onNodeClick={(node: any) => handleNodeClick(node as ExplorerNode)}
              onNodeRightClick={(node: any) => !busy && expandNode(node as ExplorerNode)}
            />
          )}
        </div>

        {selected && (
          <div
            className="w-[380px] shrink-0 overflow-auto rounded-lg border bg-card p-3"
            style={{ height: PANEL_HEIGHT }}
          >
            <div className="mb-2 flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">{selected.label}</div>
                <div className="text-xs text-muted-foreground">
                  {typeCaption(typeOfNodeId(selected.id), typeLabels).label} ·{" "}
                  <span className="font-mono">{typeOfNodeId(selected.id)}</span>
                </div>
                <div className="mt-1 break-all font-mono text-[10px] text-muted-foreground">
                  {rawIdOfNodeId(selected.id)}
                </div>
              </div>
              <button className="rounded p-1 hover:bg-muted" onClick={() => setSelected(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mb-3 flex gap-2">
              <button
                className="flex-1 rounded-md border px-2 py-1 text-xs"
                onClick={() => expandNode(selected)}
                disabled={busy}
              >
                Umfeld ausklappen
              </button>
              <button
                className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs"
                onClick={() => (journeyActive ? stopJourney() : startJourney(selected))}
                disabled={busy}
                title="Zehn Ebenen holen und die Ballungen abfliegen"
              >
                {journeyLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : journeyActive ? (
                  <Square className="h-3.5 w-3.5 fill-current" />
                ) : (
                  <Rocket className="h-3.5 w-3.5" />
                )}
                {journeyLoading ? "Reise …" : journeyActive ? "Stopp" : "Reise"}
              </button>
            </div>

            <table className="w-full table-fixed text-xs">
              <tbody>
                {attributeRows.map(row => {
                  const formatted = formatAttributeValue(row.value);
                  return (
                    <tr key={row.name} className="border-b align-top last:border-0">
                      <td className="w-[40%] py-1 pr-2 font-medium">
                        {row.label && row.label !== row.name ? (
                          <>
                            {row.label}
                            {/* Der technische Name bleibt sichtbar: er ist das, was in einer
                                Abfrage steht. */}
                            <span className="ml-1 font-mono text-[10px] text-muted-foreground">
                              {row.name}
                            </span>
                          </>
                        ) : (
                          row.name
                        )}
                        {!row.declared && (
                          // Im Bestand vorhanden, im Modell nicht — genau das will man sehen.
                          <span className="ml-1 text-[10px] text-amber-500">nicht deklariert</span>
                        )}
                      </td>
                      <td className="py-1">
                        <pre className="whitespace-pre-wrap break-all font-mono text-[11px]">
                          {formatted.text}
                        </pre>
                        {formatted.truncated && (
                          <span className="text-[10px] text-muted-foreground">(gekürzt)</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {typeDescription && (
              <div className="mt-4">
                <div className="mb-1 text-xs font-semibold">Relationen des Typs</div>
                <ul className="space-y-0.5 text-[11px] text-muted-foreground">
                  {typeDescription.relations.map(relation => (
                    <li key={relation.name}>
                      {relation.label && relation.label !== relation.name ? `${relation.label} ` : ""}
                      <span className="font-mono">{relation.name}</span> →{" "}
                      {typeCaption(relation.target, typeLabels).label}{" "}
                      <span className="font-mono">{relation.target}</span>
                      {relation.polymorphic?.length ? ` (${relation.polymorphic.join(", ")})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
